/**
 * Scope effectif côté client — miroir de `require_scope_context` (backend
 * `anm-auth/src/middleware.rs`) : le scope ancré dans le JWT, resserré par les
 * en-têtes `X-Mssp-Scope` / `X-Tenant-Scope` que le `ScopeSelector` pose.
 *
 * Sert aux vues dont la **clé métier n'est unique qu'à l'intérieur d'un
 * tenant**. Le cas d'école est la vue IP : `10.0.0.1` chez deux tenants d'un
 * même MSSP désigne deux machines distinctes. Une page identifiée par la seule
 * adresse fusionnerait leurs anomalies et leurs flux, et pourrait attribuer à
 * l'une la sonde, la trusted source ou la policy de l'autre. Sous scope tenant
 * l'ambiguïté disparaît ; au-dessus, la page doit refuser de répondre plutôt
 * que de trancher au hasard.
 */

/**
 * Scope effectif complet : `{ kind, msspId, tenantId }`.
 *
 * Un token v1 legacy (pas de `role_v2`) est résolu côté backend par
 * `Claims::base_scope` vers le tenant par défaut → `"tenant"` ici aussi.
 * Un `tenant_*` ne peut pas élargir : tout override est rejeté 403, donc son
 * scope vient toujours des claims.
 */
export function effectiveScope(user, scopeOverride = {}) {
  const role = user?.role_v2 ?? null;
  if (!role || role.startsWith("tenant_")) {
    return {
      kind: "tenant",
      msspId: user?.mssp_id ?? null,
      tenantId: user?.tenant_id ?? null,
    };
  }
  // mssp_* / platform_* : l'override resserre. Un mssp_* garde son MSSP de
  // claim quand il n'a pas choisi (il ne peut de toute façon pas en changer).
  const msspId =
    scopeOverride?.mssp_id ?? (role.startsWith("mssp_") ? (user?.mssp_id ?? null) : null);
  const tenantId = scopeOverride?.tenant_id ?? null;
  if (tenantId) return { kind: "tenant", msspId, tenantId };
  return { kind: role.startsWith("mssp_") ? "mssp" : "platform", msspId, tenantId: null };
}

/** `"tenant"` | `"mssp"` | `"platform"`. */
export function effectiveScopeKind(user, scopeOverride) {
  return effectiveScope(user, scopeOverride).kind;
}

/** Le scope effectif désigne-t-il un tenant unique ? */
export function isTenantScoped(user, scopeOverride) {
  return effectiveScopeKind(user, scopeOverride) === "tenant";
}

/**
 * Clé d'identité du scope, stable et comparable — à mettre dans les
 * dépendances d'un fetch et à comparer à celle des données en state.
 *
 * Sans elle, un passage **tenant A → tenant B** ne change pas le booléen
 * « suis-je tenant-scopé ? » : l'effet ne se relance pas et la page continue
 * d'afficher les données de A sous le libellé de B (revue PR #482 P1). Le
 * `kind` seul ne suffit pas non plus — il est identique dans les deux cas.
 */
export function scopeKeyOf(user, scopeOverride) {
  const s = effectiveScope(user, scopeOverride);
  return `${s.kind}:${s.msspId ?? ""}:${s.tenantId ?? ""}`;
}
